import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getRepublishSection, RepublishSectionAnswer } from "api/fastApi/republishSection";
import { useAudioStateContext } from "hooks/useAudioStateContext";

/** Во что превратится блок, если тронуть ещё и эту реплику. */
export interface Widening {
    /** true — правка этой строки раздвинет границы блока. */
    grows: boolean;
    count: number;
    fromMs: number;
    toMs: number;
}

export type RowSectionState = "in" | "out" | "rebuilding";

interface SectionContextValue {
    answer: RepublishSectionAnswer | null;
    loading: boolean;
    /** Состояние строки: в блоке / вне блока / папка сдана и пересобирается. */
    rowState: (cueId: string) => RowSectionState;
    /** Что будет с блоком от правки этой строки. null — посчитать не по чему. */
    widening: (cueId: string) => Widening | null;
    refresh: () => void;
}

const SectionContext = createContext<SectionContextValue>({
    answer: null,
    loading: false,
    rowState: () => "out",
    widening: () => null,
    refresh: () => undefined,
});

export const useRepublishSection = () => useContext(SectionContext);

/**
 * Границы блока пересборки, живьём.
 *
 * Смысл отметки — не запрет, а приглашение: блок всё равно перезаливается целиком, поэтому все
 * правки в нём выгодно сделать за один раз. Поэтому здесь два ответа на каждую строку: она уже
 * внутри блока — или её правка блок раздвинет, и переводчик видит, насколько.
 *
 * Расширение считается на месте, по присланному порядку реплик: спрашивать бэкенд про каждую из
 * сотни строк — сотня запросов на каждое сохранение. Правило то же, что на сервере: границы блока
 * это min/max по порядку, поэтому ответы совпадают (проверено тестом на стороне бэкенда).
 */
export const RepublishSectionProvider = ({ children }: { children: React.ReactNode }) => {
    const state = useAudioStateContext();
    const [answer, setAnswer] = useState<RepublishSectionAnswer | null>(null);
    const [loading, setLoading] = useState(false);
    const generation = useRef(0);

    const mediaId = state.mediaId;
    const folder = state.folder;
    const lang = state.lang;
    const originalLang = state.originalLang;
    const isTranslated = Boolean(lang && originalLang && lang !== originalLang);

    const load = useCallback(async () => {
        if (!mediaId || !folder || !isTranslated) {
            setAnswer(null);
            return;
        }
        const mine = ++generation.current;
        setLoading(true);
        try {
            const res = await getRepublishSection({
                mediaId,
                folderNum: Number(folder),
                lang: lang as string,
                languageFrom: originalLang as string,
            });
            if (mine === generation.current) setAnswer(res);
        } catch {
            if (mine === generation.current) setAnswer(null);
        } finally {
            if (mine === generation.current) setLoading(false);
        }
    }, [mediaId, folder, lang, originalLang, isTranslated]);

    useEffect(() => {
        load();
    }, [load]);

    // После каждого сохранения границы могли сдвинуться — переводчик должен увидеть это сразу,
    // а не после перезагрузки страницы.
    const prevSaving = useRef(state.autoSaveStatus);
    useEffect(() => {
        const was = prevSaving.current;
        prevSaving.current = state.autoSaveStatus;
        if (was === "saving" && state.autoSaveStatus !== "saving") load();
    }, [state.autoSaveStatus, load]);

    const prevEditing = useRef(state.savingStatus);
    useEffect(() => {
        const was = prevEditing.current;
        prevEditing.current = state.savingStatus;
        if (was && !state.savingStatus) load();
    }, [state.savingStatus, load]);

    const value = useMemo<SectionContextValue>(() => {
        const now = answer?.now || null;
        // Строка редактора зовётся «25_20250827211604194340», а реплика на диске —
        // «20250827211604194340»: редактор приписывает номер папки. Сверять надо по одному
        // виду, иначе отметка не совпадёт НИ РАЗУ (поймано на живой папке 25).
        const bare = (cueId: string) => {
            const cut = cueId.indexOf("_");
            return cut > 0 && /^\d+$/.test(cueId.slice(0, cut)) ? cueId.slice(cut + 1) : cueId;
        };
        const rebuilding = answer?.rebuilding;
        const inSection = new Set((now?.cue_ids || []).map(bare));
        const beingRebuilt = new Set(
            (rebuilding && (rebuilding.state === "queued" || rebuilding.state === "running")
                ? rebuilding.cue_ids
                : []
            ).map(bare),
        );
        const order = (now?.ordered || []).map(bare);
        const index = new Map(order.map((cueId, i) => [cueId, i]));

        return {
            answer,
            loading,
            rowState: (cueId: string) => {
                const key = bare(cueId);
                if (beingRebuilt.has(key)) return "rebuilding";
                return inSection.has(key) ? "in" : "out";
            },
            widening: (cueId: string) => {
                if (!now || !order.length) return null;
                const mine = index.get(bare(cueId));
                if (mine === undefined) return null;
                if (!now.cue_ids.length) {
                    // Блока ещё нет: эта правка и создаст его — из одной реплики.
                    return { grows: true, count: 1, fromMs: 0, toMs: 0 };
                }
                const first = index.get(bare(now.cue_ids[0]));
                const last = index.get(bare(now.cue_ids[now.cue_ids.length - 1]));
                if (first === undefined || last === undefined) return null;
                if (mine >= first && mine <= last) {
                    return {
                        grows: false,
                        count: now.cue_ids.length,
                        fromMs: now.from_ms,
                        toMs: now.to_ms,
                    };
                }
                const nextFirst = Math.min(first, mine);
                const nextLast = Math.max(last, mine);
                return {
                    grows: true,
                    count: nextLast - nextFirst + 1,
                    fromMs: now.from_ms,
                    toMs: now.to_ms,
                };
            },
            refresh: load,
        };
    }, [answer, loading, load]);

    return <SectionContext.Provider value={value}>{children}</SectionContext.Provider>;
};
